import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, Trash2, FileText, X, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESI = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic"
];
const MESI_FULL = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const fmt = (v) => v != null ? `€ ${parseFloat(v).toFixed(2)}` : "—";

const DatiAIPanel = ({ dati }) => (
  <div className="px-3 pb-3 border-t border-border bg-violet-500/5">
    <p className="text-[10px] uppercase tracking-wider text-violet-400 font-bold mt-2 mb-2">Dati estratti AI</p>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
      {dati.nome_dipendente && (
        <div className="col-span-2 flex justify-between">
          <span className="text-muted-foreground">Dipendente</span>
          <span className="font-medium text-foreground">{dati.nome_dipendente}</span>
        </div>
      )}
      {dati.qualifica && (
        <div className="col-span-2 flex justify-between">
          <span className="text-muted-foreground">Qualifica</span>
          <span className="font-medium text-foreground">{dati.qualifica}</span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-muted-foreground">Lordo</span>
        <span className="font-semibold text-foreground">{fmt(dati.retribuzione_lorda)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Netto</span>
        <span className="font-semibold text-emerald-600">{fmt(dati.retribuzione_netta)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">INPS</span>
        <span className="text-foreground">{fmt(dati.contributi_inps)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">IRPEF</span>
        <span className="text-foreground">{fmt(dati.irpef)}</span>
      </div>
      {dati.addizionale_regionale != null && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Add. Reg.</span>
          <span className="text-foreground">{fmt(dati.addizionale_regionale)}</span>
        </div>
      )}
      {dati.addizionale_comunale != null && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Add. Com.</span>
          <span className="text-foreground">{fmt(dati.addizionale_comunale)}</span>
        </div>
      )}
      {dati.ore_lavorate != null && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Ore lav.</span>
          <span className="text-foreground">{dati.ore_lavorate}h</span>
        </div>
      )}
      {dati.giorni_lavorati != null && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Giorni lav.</span>
          <span className="text-foreground">{dati.giorni_lavorati}gg</span>
        </div>
      )}
      {dati.tfr_maturato != null && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">TFR mat.</span>
          <span className="text-foreground">{fmt(dati.tfr_maturato)}</span>
        </div>
      )}
    </div>
    {dati.note && (
      <p className="text-xs text-muted-foreground mt-2 italic">{dati.note}</p>
    )}
  </div>
);

export const BustePagaDialog = ({ open, onOpenChange, operatore, token }) => {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [meseAttivo, setMeseAttivo] = useState(now.getMonth() + 1);
  const [buste, setBuste] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [estraendo, setEstraendo] = useState(null); // busta_id in corso
  const [datiAperti, setDatiAperti] = useState({}); // {busta_id: boolean}

  const fetchBuste = useCallback(async () => {
    if (!operatore?.id || !open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buste-paga`, {
        params: { dipendente_id: operatore.id, anno },
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuste(res.data);
    } catch {
      toast.error("Errore nel caricamento buste paga");
    } finally {
      setLoading(false);
    }
  }, [operatore?.id, anno, token, open]);

  useEffect(() => {
    fetchBuste();
  }, [fetchBuste]);

  const busteMese = useMemo(
    () => buste.filter(b => b.mese === meseAttivo),
    [buste, meseAttivo]
  );

  const mesiConBuste = useMemo(() => {
    const set = new Set();
    buste.forEach(b => set.add(b.mese));
    return set;
  }, [buste]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await axios.post(`${API}/buste-paga`, {
          dipendente_id: operatore.id,
          anno,
          mese: meseAttivo,
          file_data: reader.result,
          file_name: file.name
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Busta paga ${MESI_FULL[meseAttivo - 1]} ${anno} caricata`);
        fetchBuste();
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error("Errore nel caricamento");
      setUploading(false);
    }
    e.target.value = "";
  };

  const handleEstraiAI = async (busta) => {
    if (estraendo) return;
    setEstraendo(busta.id);
    try {
      const res = await axios.post(
        `${API}/buste-paga/${busta.id}/estrai-ai`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Aggiorna la busta localmente con i dati estratti
      setBuste(prev => prev.map(b =>
        b.id === busta.id ? { ...b, dati_estratti_ai: res.data.dati, ai_estratto_at: new Date().toISOString() } : b
      ));
      setDatiAperti(prev => ({ ...prev, [busta.id]: true }));
      toast.success("Dati estratti con AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore estrazione AI");
    } finally {
      setEstraendo(null);
    }
  };

  const handleDelete = async (bustaId) => {
    try {
      await axios.delete(`${API}/buste-paga/${bustaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Busta paga eliminata");
      fetchBuste();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownload = async (busta) => {
    const token = (window.localStorage && window.localStorage.getItem("token")) || "";
    const baseUrl = `${API.replace('/api', '')}${busta.file_url}`;
    const fileUrl = token ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : baseUrl;
    const fileName = busta.file_nome || `busta_${busta.mese_label}_${busta.anno}.pdf`;
    if (navigator.share) {
      try {
        const res = await fetch(fileUrl);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: blob.type });
        await navigator.share({ files: [file], title: fileName });
        return;
      } catch {}
    }
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-base font-bold">
            Buste Paga — {operatore?.nome || operatore?.email || operatore?.telefono}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pb-20">
          {/* Anno selector */}
          <div className="flex items-center justify-center gap-4 p-3 border-b border-border bg-muted/30">
            <button
              onClick={() => setAnno(a => a - 1)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-background border border-border hover:bg-muted active:scale-95 transition-all"
              data-testid="buste-paga-anno-prev"
            >
              {anno - 1}
            </button>
            <span className="text-lg font-bold text-foreground min-w-[60px] text-center" data-testid="buste-paga-anno-current">{anno}</span>
            <button
              onClick={() => setAnno(a => a + 1)}
              disabled={anno >= now.getFullYear()}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-background border border-border hover:bg-muted active:scale-95 transition-all disabled:opacity-30"
              data-testid="buste-paga-anno-next"
            >
              {anno + 1}
            </button>
          </div>

          {/* Month selector - horizontal scroll */}
          <div className="overflow-x-auto hide-scrollbar border-b border-border">
            <div className="flex gap-1.5 p-3 min-w-max">
              {MESI.map((m, idx) => {
                const mese = idx + 1;
                const isActive = meseAttivo === mese;
                const hasBuste = mesiConBuste.has(mese);
                return (
                  <button
                    key={mese}
                    onClick={() => setMeseAttivo(mese)}
                    className={cn(
                      "px-3 py-2 rounded-full text-xs font-semibold transition-all active:scale-95 relative",
                      isActive
                        ? "bg-primary text-white shadow-sm"
                        : "bg-muted/60 text-muted-foreground hover:bg-muted"
                    )}
                    data-testid={`buste-paga-mese-${mese}`}
                  >
                    {m}
                    {hasBuste && !isActive && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {MESI_FULL[meseAttivo - 1]} {anno}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : busteMese.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessuna busta paga per questo mese</p>
              </div>
            ) : (
              <div className="space-y-2">
                {busteMese.map(busta => (
                  <div
                    key={busta.id}
                    className="rounded-xl bg-card border border-border overflow-hidden"
                    data-testid={`busta-paga-item-${busta.id}`}
                  >
                    {/* Riga principale */}
                    <div className="flex items-center gap-3 p-3">
                      <button
                        onClick={() => handleDownload(busta)}
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 transition-colors active:scale-95 shadow-sm"
                        title="Scarica / Condividi"
                        data-testid={`busta-download-${busta.id}`}
                      >
                        <Download className="h-5 w-5" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {busta.file_nome || `Busta ${busta.mese_label}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(busta.created_at).toLocaleDateString("it-IT")}
                          {busta.ai_estratto_at && (
                            <span className="ml-2 text-violet-500 font-medium">AI</span>
                          )}
                        </p>
                      </div>
                      {/* Bottone Estrai AI */}
                      <button
                        onClick={() => busta.dati_estratti_ai
                          ? setDatiAperti(prev => ({ ...prev, [busta.id]: !prev[busta.id] }))
                          : handleEstraiAI(busta)
                        }
                        disabled={estraendo === busta.id}
                        className={cn(
                          "h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all active:scale-95",
                          busta.dati_estratti_ai
                            ? "bg-violet-500/10 text-violet-500 hover:bg-violet-500/20"
                            : "bg-violet-600 text-white hover:bg-violet-700"
                        )}
                        data-testid={`busta-ai-${busta.id}`}
                      >
                        {estraendo === busta.id ? (
                          <span className="animate-spin w-3 h-3 rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        {busta.dati_estratti_ai
                          ? (datiAperti[busta.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
                          : <span className="ml-0.5">AI</span>
                        }
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        onClick={() => handleDelete(busta.id)}
                        data-testid={`busta-delete-${busta.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Pannello dati estratti AI */}
                    {busta.dati_estratti_ai && datiAperti[busta.id] && (
                      <DatiAIPanel dati={busta.dati_estratti_ai} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer - Upload button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
          <label className={cn(
            "flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all active:scale-[0.98]",
            uploading
              ? "bg-muted text-muted-foreground pointer-events-none"
              : "bg-primary text-white hover:bg-primary/90 shadow-sm"
          )}>
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="buste-paga-upload-input"
            />
            <Upload className="h-4 w-4" />
            {uploading ? "Caricamento..." : `Carica Busta — ${MESI_FULL[meseAttivo - 1]} ${anno}`}
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
};
